import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Upload, FileText, Eye, Settings, PlayCircle,
  AlertCircle, CheckCircle2, Info, Download,
  ChevronRight, X, Loader2, Table, BarChart3
} from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { FormField, Input, Select } from '../ui/Form';
import { ImportStatusBadge } from '../ui/ImportStatusBadge';
import { useDataExchange } from '../../hooks/useDataExchange';
import type { FiscalYear } from '../../types/fiscal-year';
import type {
  ImportConfig,
  PreviewResult,
  FormatDetectionResult,
  AccountingStandard,
  FileFormat,
  DataType,
  FileEncoding,
  FieldSeparator,
  ValidationMode
} from '../../types/data-exchange';

const STD_LABELS: Record<AccountingStandard, string> = {
  fec: 'FEC',
  saf_t: 'SAF-T',
  xbrl: 'XBRL',
  syscohada: 'SYSCOHADA',
  ohada_revise: 'OHADA Révisé',
  generic: 'Générique',
  pennylane: 'Pennylane'
};

const DATATYPE_LABELS: Record<DataType, string> = {
  fiscal_year: 'Exercice comptable',
  journal_entries: 'Écritures comptables',
  chart_of_accounts: 'Plan comptable',
  trial_balance: 'Balance des comptes',
  general_ledger: 'Grand livre',
  invoices: 'Factures',
  third_parties: 'Tiers'
};

interface ImportDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  fiscalYear: FiscalYear;
  /** Appelé quand l'import est lancé avec succès (pour recharger les données) */
  onImportStarted?: (taskId: string) => void;
}

type Step = 'upload' | 'preview' | 'configure' | 'importing';

/**
 * Modal d'import de données comptables.
 * Workflow : Upload → Prévisualisation → Configuration → Import
 */
export function ImportDataModal({
  isOpen,
  onClose,
  fiscalYear,
  onImportStarted
}: ImportDataModalProps) {
  // ---- Steps ----
  const [step, setStep] = useState<Step>('upload');

  // ---- Upload ----
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ---- Détection ----
  const [detection, setDetection] = useState<FormatDetectionResult | null>(null);

  // ---- Preview ----
  const [preview, setPreview] = useState<PreviewResult | null>(null);

  // ---- Config ----
  const [config, setConfig] = useState<ImportConfig>({
    dataType: 'journal_entries',
    fileFormat: 'csv',
    accountingStandard: 'generic',
    encoding: 'utf-8',
    separator: '|',
    hasHeader: true,
    createMissingAccounts: true,
    validationMode: 'lenient',
    continueOnError: true,
    maxErrors: 100
  });

  const {
    loading,
    error,
    detectFormat,
    previewFile,
    importUpload,
    activeTask,
    stopPolling,
    cancelTask,
    downloadTemplate
  } = useDataExchange();

  // Reset quand on ferme
  useEffect(() => {
    if (!isOpen) {
      setStep('upload');
      setSelectedFile(null);
      setDetection(null);
      setPreview(null);
      setDragOver(false);
      stopPolling();
    }
  }, [isOpen, stopPolling]);

  // ---- Handlers Upload ----

  const handleFileSelect = useCallback(async (file: File) => {
    setSelectedFile(file);
    setDetection(null);
    setPreview(null);

    // Déduire le fileFormat depuis l'extension
    const detectedFileFormat = getFileFormatFromExtension(file.name);
    setConfig(prev => ({ ...prev, fileFormat: detectedFileFormat }));

    // Auto-détection du standard via le backend
    const result = await detectFormat(file);
    if (result) {
      setDetection(result);
      // recommendedFormat du backend = standard comptable OU format technique
      // On mappe intelligemment :
      const rec = result.recommendedFormat?.toLowerCase() || '';
      const knownStandards: AccountingStandard[] = ['fec', 'saf_t', 'xbrl', 'syscohada', 'ohada_revise', 'pennylane'];
      const inferredStandard: AccountingStandard = knownStandards.includes(rec as AccountingStandard)
        ? (rec as AccountingStandard)
        : 'generic';

      setConfig(prev => ({
        ...prev,
        fileFormat: detectedFileFormat,
        accountingStandard: inferredStandard
      }));
    }
  }, [detectFormat]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => setDragOver(false), []);

  // ---- Preview ----

  const handlePreview = useCallback(async () => {
    if (!selectedFile) return;
    const result = await previewFile(selectedFile, detection?.recommendedFormat);
    if (result) {
      setPreview(result);
      setStep('preview');
    }
  }, [selectedFile, detection, previewFile]);

  // ---- Import ----

  const handleStartImport = useCallback(async () => {
    if (!selectedFile) {
      console.warn('⚠️ handleStartImport: pas de fichier sélectionné');
      return;
    }
    console.log('🚀 handleStartImport appelé', {
      file: selectedFile.name,
      fiscalYearId: fiscalYear?.id,
      config
    });
    setStep('importing');

    // S'assurer que fileFormat est le format technique du fichier
    // et accountingStandard est bien un standard comptable valide
    const finalConfig: ImportConfig = {
      ...config,
      fileFormat: config.fileFormat || getFileFormatFromExtension(selectedFile.name),
      // Ne jamais envoyer un format technique comme accountingStandard
      accountingStandard: config.accountingStandard || 'generic'
    };

    console.log('🚀 Appel importUpload avec:', { fiscalYearId: fiscalYear.id, fileName: selectedFile.name, finalConfig });
    const success = await importUpload(fiscalYear.id, selectedFile, finalConfig);
    if (success) {
      onImportStarted?.('direct-upload');
    }
  }, [selectedFile, config, fiscalYear, importUpload, onImportStarted]);

  // ---- Render steps ----

  const steps: { key: Step; label: string; icon: React.ElementType }[] = [
    { key: 'upload', label: 'Fichier', icon: Upload },
    { key: 'preview', label: 'Aperçu', icon: Eye },
    { key: 'configure', label: 'Options', icon: Settings },
    { key: 'importing', label: 'Import', icon: PlayCircle }
  ];

  const currentStepIdx = steps.findIndex(s => s.key === step);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Importer des données comptables"
      size="xl"
    >
      <div className="space-y-5">
        {/* Indicateur d'étapes */}
        <div className="flex items-center justify-center gap-1">
          {steps.map((s, i) => {
            const StepIcon = s.icon;
            const isActive = i === currentStepIdx;
            const isDone = i < currentStepIdx;
            return (
              <React.Fragment key={s.key}>
                {i > 0 && (
                  <div className={`w-8 h-px ${isDone ? 'bg-primary' : 'bg-border'}`} />
                )}
                <div
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : isDone
                        ? 'text-primary'
                        : 'text-muted-foreground'
                  }`}
                >
                  <StepIcon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{s.label}</span>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Exercice cible */}
        <div className="flex items-center gap-2 text-sm bg-muted/50 rounded-md px-3 py-2">
          <Info className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span className="text-muted-foreground">Exercice cible :</span>
          <span className="font-medium text-foreground">{fiscalYear.code}</span>
          <span className="text-muted-foreground">
            ({new Date(fiscalYear.startDate).toLocaleDateString('fr-FR')} — {new Date(fiscalYear.endDate).toLocaleDateString('fr-FR')})
          </span>
        </div>

        {/* Erreur */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* STEP: Upload */}
        {step === 'upload' && renderUploadStep()}

        {/* STEP: Preview */}
        {step === 'preview' && renderPreviewStep()}

        {/* STEP: Configure */}
        {step === 'configure' && renderConfigureStep()}

        {/* STEP: Importing */}
        {step === 'importing' && renderImportingStep()}
      </div>
    </Modal>
  );

  // ========== RENDER FUNCTIONS ==========

  function renderUploadStep() {
    return (
      <div className="space-y-4">
        {/* Zone de drop */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragOver
              ? 'border-primary bg-primary/5'
              : selectedFile
                ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10'
                : 'border-gray-300 dark:border-gray-600 hover:border-primary/50'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".csv,.txt,.xlsx,.xls,.xml,.json"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }}
          />

          {selectedFile ? (
            <div className="space-y-2">
              <div className="flex items-center justify-center gap-2">
                <FileText className="w-8 h-8 text-green-600 dark:text-green-400" />
                <div className="text-left">
                  <p className="font-medium text-foreground">{selectedFile.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(selectedFile.size / 1024).toFixed(1)} Ko
                  </p>
                </div>
                <button
                  onClick={() => { setSelectedFile(null); setDetection(null); setPreview(null); }}
                  className="ml-2 text-muted-foreground hover:text-destructive p-1 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Résultat détection */}
              {loading && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Analyse du fichier...</span>
                </div>
              )}
              {detection && (
                <div className="flex items-center justify-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <span className="text-green-700 dark:text-green-400">
                    Format détecté : <strong>{detection.recommendedFormat.toUpperCase()}</strong> ({detection.confidence}% confiance)
                  </span>
                </div>
              )}
            </div>
          ) : (
            <>
              <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
              <Button
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
              >
                Sélectionner un fichier
              </Button>
              <p className="mt-2 text-sm text-muted-foreground">
                ou glisser-déposer ici
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                CSV, TXT, XLSX, XLS, XML, JSON
              </p>
            </>
          )}
        </div>

        {/* Templates téléchargeables */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Download className="w-3.5 h-3.5" />
          <span>Exemples :</span>
          {['fec', 'csv', 'syscohada'].map(fmt => (
            <button
              key={fmt}
              onClick={() => downloadTemplate(fmt)}
              className="text-primary hover:underline"
            >
              {fmt.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          {selectedFile && detection && (
            <Button onClick={handlePreview} isLoading={loading} icon={Eye}>
              Prévisualiser
            </Button>
          )}
          {selectedFile && detection && (
            <Button
              variant="secondary"
              onClick={() => setStep('configure')}
              icon={Settings}
            >
              Configurer directement
            </Button>
          )}
        </div>
      </div>
    );
  }

  function renderPreviewStep() {
    if (!preview) return null;

    return (
      <div className="space-y-4">
        {/* Statistiques rapides */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Lignes" value={preview.statistics.totalLines} icon={Table} />
          <StatCard
            label="Débits"
            value={formatMoney(preview.statistics.totalDebit)}
            icon={BarChart3}
          />
          <StatCard
            label="Crédits"
            value={formatMoney(preview.statistics.totalCredit)}
            icon={BarChart3}
          />
          <StatCard
            label="Équilibré"
            value={preview.statistics.isBalanced ? 'Oui' : 'Non'}
            icon={preview.statistics.isBalanced ? CheckCircle2 : AlertCircle}
            variant={preview.statistics.isBalanced ? 'success' : 'error'}
          />
        </div>

        {/* Période détectée */}
        {preview.statistics.dateRange && (
          <div className="text-xs text-muted-foreground">
            Période : {preview.statistics.dateRange.start} — {preview.statistics.dateRange.end}
            {preview.statistics.journalCodes.length > 0 && (
              <span className="ml-3">
                Journaux : {preview.statistics.journalCodes.join(', ')}
              </span>
            )}
          </div>
        )}

        {/* Tableau d'aperçu */}
        <div className="border border-border rounded-lg overflow-x-auto max-h-64">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 sticky top-0">
              <tr>
                {preview.columnHeaders.map(h => (
                  <th key={h} className="px-2 py-1.5 text-left font-medium text-muted-foreground whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {preview.preview.map((row, i) => (
                <tr key={i} className="hover:bg-muted/30">
                  {preview.columnHeaders.map((h, colIdx) => {
                    // Le backend peut retourner originalData comme objet { headerName: value }
                    // ou comme tableau aligné avec columnHeaders
                    let val: unknown = undefined;
                    if (row.originalData) {
                      if (Array.isArray(row.originalData)) {
                        val = row.originalData[colIdx];
                      } else {
                        // Objet avec clés = noms de colonnes
                        val = (row.originalData as Record<string, unknown>)[h];
                      }
                    }
                    if (val === undefined) {
                      val = (row as any)[h];
                    }
                    return (
                      <td key={h} className="px-2 py-1 whitespace-nowrap text-foreground">
                        {val != null ? String(val) : ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground text-right">
          Affichage de {preview.totalPreviewLines} / {preview.statistics.totalLines} lignes
        </p>

        {/* Actions */}
        <div className="flex justify-between">
          <Button variant="secondary" onClick={() => setStep('upload')}>Retour</Button>
          <Button onClick={() => setStep('configure')} icon={ChevronRight}>
            Configurer l'import
          </Button>
        </div>
      </div>
    );
  }

  function renderConfigureStep() {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="Type de données">
            <Select
              value={config.dataType || 'journal_entries'}
              onChange={(e) => setConfig(prev => ({ ...prev, dataType: e.target.value as DataType }))}
            >
              {Object.entries(DATATYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </Select>
          </FormField>

          <FormField label="Standard comptable">
            <Select
              value={config.accountingStandard || 'generic'}
              onChange={(e) => setConfig(prev => ({ ...prev, accountingStandard: e.target.value as AccountingStandard }))}
            >
              {Object.entries(STD_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </Select>
          </FormField>

          <FormField label="Encodage">
            <Select
              value={config.encoding || 'utf-8'}
              onChange={(e) => setConfig(prev => ({ ...prev, encoding: e.target.value as FileEncoding }))}
            >
              <option value="utf-8">UTF-8</option>
              <option value="utf-8-bom">UTF-8 BOM</option>
              <option value="latin1">Latin-1</option>
              <option value="iso-8859-1">ISO 8859-1</option>
              <option value="windows-1252">Windows-1252</option>
            </Select>
          </FormField>

          <FormField label="Séparateur">
            <Select
              value={config.separator || '|'}
              onChange={(e) => setConfig(prev => ({ ...prev, separator: e.target.value as FieldSeparator }))}
            >
              <option value="|">Pipe ( | )</option>
              <option value=";">Point-virgule ( ; )</option>
              <option value=",">Virgule ( , )</option>
              <option value={'\t'}>Tabulation</option>
            </Select>
          </FormField>

          <FormField label="Mode de validation">
            <Select
              value={config.validationMode || 'lenient'}
              onChange={(e) => setConfig(prev => ({ ...prev, validationMode: e.target.value as ValidationMode }))}
            >
              <option value="lenient">Souple (recommandé)</option>
              <option value="strict">Strict</option>
            </Select>
          </FormField>

          <FormField label="Max erreurs autorisées">
            <Input
              type="number"
              value={config.maxErrors || 100}
              onChange={(e) => setConfig(prev => ({ ...prev, maxErrors: parseInt(e.target.value) || 100 }))}
              min={1}
              max={10000}
            />
          </FormField>
        </div>

        {/* Options booléennes */}
        <div className="space-y-2">
          <ToggleOption
            label="Le fichier contient un en-tête"
            checked={config.hasHeader ?? true}
            onChange={(v) => setConfig(prev => ({ ...prev, hasHeader: v }))}
          />
          <ToggleOption
            label="Créer les comptes manquants automatiquement"
            checked={config.createMissingAccounts ?? true}
            onChange={(v) => setConfig(prev => ({ ...prev, createMissingAccounts: v }))}
          />
          <ToggleOption
            label="Continuer en cas d'erreur"
            checked={config.continueOnError ?? true}
            onChange={(v) => setConfig(prev => ({ ...prev, continueOnError: v }))}
          />
        </div>

        {/* Actions */}
        <div className="flex justify-between">
          <Button variant="secondary" onClick={() => setStep(preview ? 'preview' : 'upload')}>
            Retour
          </Button>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>Annuler</Button>
            <Button onClick={handleStartImport} isLoading={loading} icon={PlayCircle}>
              Lancer l'import
            </Button>
          </div>
        </div>
      </div>
    );
  }

  function renderImportingStep() {
    return (
      <div className="space-y-4">
        {activeTask ? (
          <ImportStatusBadge
            status={activeTask.status}
            progress={activeTask.progress}
            currentStep={activeTask.currentStep}
            processedLines={activeTask.processedLines}
            totalLines={activeTask.totalLines}
            errorCount={activeTask.errorCount}
            onCancel={() => cancelTask(activeTask.taskId)}
          />
        ) : loading ? (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <p className="text-sm text-muted-foreground">Import en cours...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-6 gap-3">
            <AlertCircle className="w-8 h-8 text-red-500" />
            <p className="text-sm font-medium text-red-600 dark:text-red-400">Import échoué</p>
            <p className="text-xs text-muted-foreground text-center max-w-md">{error}</p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
            <p className="text-sm font-medium text-foreground">Import terminé</p>
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={onClose}>
            {loading ? 'Fermer' : 'Terminé'}
          </Button>
        </div>
      </div>
    );
  }
}

// ========== SOUS-COMPOSANTS ==========

function StatCard({
  label,
  value,
  icon: Icon,
  variant = 'default'
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  variant?: 'default' | 'success' | 'error';
}) {
  const colors = {
    default: 'text-muted-foreground',
    success: 'text-green-600 dark:text-green-400',
    error: 'text-red-600 dark:text-red-400'
  };

  return (
    <div className="bg-muted/30 rounded-lg p-2.5 text-center">
      <Icon className={`w-4 h-4 mx-auto mb-1 ${colors[variant]}`} />
      <div className="font-semibold text-sm text-foreground">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function ToggleOption({
  label,
  checked,
  onChange
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer text-sm">
      <div
        className={`w-8 h-4.5 rounded-full relative transition-colors cursor-pointer ${
          checked ? 'bg-primary' : 'bg-muted'
        }`}
        onClick={() => onChange(!checked)}
      >
        <div
          className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </div>
      <span className="text-foreground">{label}</span>
    </label>
  );
}

// ========== HELPERS ==========

function getFileFormatFromExtension(filename: string): FileFormat {
  const ext = filename.split('.').pop()?.toLowerCase();
  const map: Record<string, FileFormat> = {
    csv: 'csv', txt: 'txt', xlsx: 'xlsx', xls: 'xls',
    xml: 'xml', json: 'json', pdf: 'pdf'
  };
  return map[ext || ''] || 'csv';
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}
